import { Navigate, Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateFormatting';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { toast } from 'sonner';
import { useSupportNotifications } from '../../context/SupportNotificationsContext';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Building2,
  Ticket,
  LogOut,
  UserCog,
  ShieldCheck,
  Settings,
  Monitor,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
  Bell,
  HelpCircle,
  User,
  ChevronDown,
  Search,
  Rocket,
  Book,
  Sun,
  Moon,
  Mail,
  RefreshCw,
  MessageSquarePlus,
  ToggleLeft,
  Wrench,
  Megaphone,
  Database,
  Eye,
} from 'lucide-react';

const SuperAdminLayout = () => {
  const { admin, loading, logout, stopImpersonation } = useSuperAdmin();
  const {
    unreadCount: unreadSupportCount,
    unreadTickets: unreadSupportTickets,
    markRead: markSupportTicketRead,
  } = useSupportNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('equipr_sa_sidebar_collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('equipr_sa_theme');
    return saved || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('equipr_sa_sidebar_collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem('equipr_sa_theme', theme);
    // Sync the SA panel theme to the <html> class so all shadcn CSS variables
    // (foreground/background/border/etc.) switch correctly. Without this,
    // outline buttons and badges render with dark-mode foreground (white)
    // on the light-mode card bg and become unreadable.
    const root = document.documentElement;
    const previous = root.classList.contains('dark') ? 'dark' : 'light';
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    return () => {
      // Restore the previous (global tenant) theme class when unmounting,
      // so exiting the SA panel doesn't leak its theme to the rest of the app.
      root.classList.remove('light', 'dark');
      root.classList.add(previous);
    };
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
      toast.success('Returned to your admin session');
      navigate('/super-admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to exit impersonation');
    }
  };

  // Relative time like "5m ago" / "3h ago" / "2d ago" for notification rows
  const timeAgo = (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return formatDate(iso);
  };

  const priorityDotClass = (p) => {
    switch ((p || '').toLowerCase()) {
      case 'urgent': return 'bg-red-500';
      case 'high':   return 'bg-orange-500';
      case 'medium': return 'bg-[#ffd108]';
      default:       return 'bg-slate-400';
    }
  };

  const handleNotificationClick = (ticketId) => {
    markSupportTicketRead(ticketId);
    navigate(`/super-admin/tickets/${ticketId}`);
  };

  const handleClearNotification = (e, ticketId) => {
    e.stopPropagation();
    e.preventDefault();
    markSupportTicketRead(ticketId);
  };

  const handleClearAllNotifications = () => {
    const ids = (unreadSupportTickets || []).map((t) => t.id);
    ids.forEach((id) => markSupportTicketRead(id));
  };

  // Theme-aware classes
  const isDark = theme === 'dark';
  const themeClasses = {
    // Main backgrounds — light mode keeps the same slate/navy character as
    // dark mode, just lifted to near-white so it feels like the Super Admin
    // palette instead of switching to tenant-portal charcoal.
    pageBg: isDark ? 'bg-slate-950' : 'bg-slate-50',
    headerBg: isDark ? 'bg-slate-900 border-slate-800' : 'bg-[#B0BCCC] border-slate-500',
    sidebarBg: isDark ? 'bg-slate-900' : 'bg-[#B0BCCC] border-r border-slate-500',
    // Text colors
    textPrimary: isDark ? 'text-white' : 'text-slate-900',
    textSecondary: isDark ? 'text-slate-400' : 'text-slate-800',
    textMuted: isDark ? 'text-slate-500' : 'text-slate-700',
    // Interactive elements
    navItem: isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-800 hover:bg-slate-500/50 hover:text-slate-950',
    navItemActive: isDark ? 'bg-slate-800 text-white' : 'bg-slate-500/60 text-slate-950',
    buttonGhost: isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-800 hover:text-slate-950 hover:bg-slate-500/50',
    // Inputs
    searchBg: isDark ? 'bg-slate-800 text-slate-500' : 'bg-white text-slate-500 border border-slate-500',
    // Borders
    borderColor: isDark ? 'border-slate-800' : 'border-slate-500',
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${themeClasses.pageBg} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/super-admin/login" replace />;
  }

  const navItems = [
    { path: '/super-admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/super-admin/tenants', icon: Building2, label: 'Tenants' },
    { path: '/super-admin/demo-tenants', icon: Building2, label: 'Demo Tenants', adminOnly: true },
    { path: '/super-admin/tickets', icon: Ticket, label: 'Support Tickets' },
    { path: '/super-admin/feedback', icon: MessageSquarePlus, label: 'EA Feedback', adminOnly: true },
    { path: '/super-admin/announcements', icon: Megaphone, label: 'Announcements', adminOnly: true },
    { path: '/super-admin/sessions', icon: Monitor, label: 'Active Sessions' },
    { path: '/super-admin/auth-migration', icon: RefreshCw, label: 'Auth Migration', adminOnly: true },
    { path: '/super-admin/feature-flags', icon: ToggleLeft, label: 'Feature Flags', adminOnly: true },
    { path: '/super-admin/migrations', icon: Wrench, label: 'Migrations', adminOnly: true },
    { path: '/super-admin/allocate-migration', icon: Database, label: 'Allocate Migration', adminOnly: true },
    { path: '/super-admin/emails', icon: Mail, label: 'Email Testing', adminOnly: true },
    { path: '/super-admin/roadmap', icon: Rocket, label: 'Roadmap', adminOnly: true },
    { path: '/super-admin/help', icon: Book, label: 'Help Center' },
    { path: '/super-admin/agents', icon: UserCog, label: 'Support Agents', adminOnly: true },
    { path: '/super-admin/security', icon: ShieldCheck, label: 'Security (2FA)' },
    { path: '/super-admin/settings', icon: Settings, label: 'System Settings', adminOnly: true },
  ];

  const filteredNavItems = navItems.filter(item => !item.adminOnly || admin.role === 'admin');

  const NavItem = ({ path, icon: Icon, label }) => {
    const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
    
    if (sidebarCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              to={path}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${
                isActive
                  ? themeClasses.navItemActive
                  : themeClasses.navItem
              }`}
            >
              <Icon className="h-5 w-5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="font-medium">
            {label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Link
        to={path}
        onClick={() => setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
          isActive
            ? themeClasses.navItemActive
            : themeClasses.navItem
        }`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="truncate">{label}</span>
      </Link>
    );
  };

  return (
    <TooltipProvider>
      <div className={`min-h-screen ${themeClasses.pageBg} flex flex-col ${isDark ? 'dark' : ''}`}>
        {/* Top Navigation Bar */}
        <header className={`fixed top-0 left-0 right-0 h-12 ${themeClasses.headerBg} border-b flex items-center justify-between px-4 z-50`}>
          {/* Left side - Logo & Mobile menu */}
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 rounded-md ${themeClasses.buttonGhost}`}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded overflow-hidden">
                <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
              </div>
              <div className="hidden sm:block">
                <span className={`font-heading font-bold text-sm ${themeClasses.textPrimary}`}>EQUIPR CORE</span>
                <span className={`text-[10px] ${themeClasses.textMuted} ml-1.5 uppercase tracking-wider`}>Super Admin</span>
              </div>
            </div>
          </div>

          {/* Center - Search placeholder */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${themeClasses.searchBg}`}>
              <Search className="h-4 w-4" />
              <span>Search tenants, tickets...</span>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-1">
            {/* Theme Toggle */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={`h-8 w-8 ${themeClasses.buttonGhost}`}
                  onClick={toggleTheme}
                  data-testid="theme-toggle-btn"
                >
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isDark ? 'Light Mode' : 'Dark Mode'}</TooltipContent>
            </Tooltip>

            {/* Help */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={`h-8 w-8 ${themeClasses.buttonGhost}`}>
                  <HelpCircle className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Help</TooltipContent>
            </Tooltip>

            {/* Notifications */}
            <DropdownMenu>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 relative ${unreadSupportCount > 0 ? 'text-[#ffd108]' : themeClasses.buttonGhost}`}
                      data-testid="sa-topbar-notifications-btn"
                    >
                      <Bell className="h-4 w-4" />
                      {unreadSupportCount > 0 && (
                        <span
                          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ffd108] text-[#1a1a1a] text-[10px] font-bold flex items-center justify-center"
                          data-testid="sa-topbar-notifications-badge"
                        >
                          {unreadSupportCount > 9 ? '9+' : unreadSupportCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  {unreadSupportCount > 0
                    ? `Notifications · ${unreadSupportCount} unread`
                    : 'Notifications'}
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent
                align="end"
                className="w-80 p-0"
                data-testid="sa-notifications-dropdown"
              >
                <div className={`flex items-center justify-between px-3 py-2 border-b ${themeClasses.borderColor}`}>
                  <span className="text-sm font-semibold">Notifications</span>
                  <div className="flex items-center gap-2">
                    {unreadSupportCount > 0 && (
                      <span className="text-[11px] text-slate-400">
                        {unreadSupportCount} unread
                      </span>
                    )}
                    {unreadSupportTickets && unreadSupportTickets.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearAllNotifications}
                        className="text-[11px] text-[#ffd108] hover:underline"
                        data-testid="sa-notifications-clear-all"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
                {(!unreadSupportTickets || unreadSupportTickets.length === 0) ? (
                  <div
                    className="px-3 py-6 text-center text-xs text-slate-400"
                    data-testid="sa-notifications-empty"
                  >
                    You're all caught up.
                  </div>
                ) : (
                  <div className="max-h-80 overflow-y-auto">
                    {unreadSupportTickets.map((t) => (
                      <div
                        key={t.id}
                        className={`group relative w-full flex gap-2 items-start border-b last:border-b-0 ${themeClasses.borderColor} ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-500/20'} transition-colors`}
                      >
                        <button
                          type="button"
                          onClick={() => handleNotificationClick(t.id)}
                          className="flex-1 text-left px-3 py-2 flex gap-2 items-start min-w-0"
                          data-testid={`sa-notification-item-${t.id}`}
                        >
                          <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${priorityDotClass(t.priority)}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-xs font-medium truncate">
                                {t.ticket_number ? `New activity · ${t.ticket_number}` : 'New activity on ticket'}
                              </span>
                              <span className="text-[10px] text-slate-400 shrink-0">
                                {timeAgo(t.updated_at)}
                              </span>
                            </div>
                            <div className={`text-xs truncate ${themeClasses.textSecondary}`}>
                              {t.subject}
                            </div>
                            {t.company_name && (
                              <div className="text-[10px] text-slate-500 truncate">
                                {t.company_name}
                              </div>
                            )}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleClearNotification(e, t.id)}
                          className={`shrink-0 mr-2 mt-2 h-5 w-5 inline-flex items-center justify-center rounded ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-500/30'} opacity-60 group-hover:opacity-100`}
                          aria-label="Clear notification"
                          data-testid={`sa-notification-clear-${t.id}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <DropdownMenuSeparator className="m-0" />
                <button
                  type="button"
                  onClick={() => navigate('/super-admin/tickets')}
                  className={`w-full text-center text-xs py-2 ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-500/20'}`}
                  data-testid="sa-notifications-view-all"
                >
                  View all support tickets
                </button>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Settings (Admin only) */}
            {admin.role === 'admin' && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={`h-8 w-8 ${themeClasses.buttonGhost}`}
                    onClick={() => navigate('/super-admin/settings')}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>System Settings</TooltipContent>
              </Tooltip>
            )}

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`h-8 gap-2 px-2 ${isDark ? 'text-slate-300 hover:text-white hover:bg-slate-800' : 'text-slate-800 hover:text-slate-950 hover:bg-slate-500/50'}`}>
                  <div className="w-6 h-6 rounded-full bg-[#ffd108] flex items-center justify-center">
                    <User className="h-3.5 w-3.5 text-black" />
                  </div>
                  <span className={`hidden md:block text-xs font-medium max-w-[100px] truncate`}>
                    {admin.full_name}
                  </span>
                  <ChevronDown className="h-3 w-3 hidden md:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{admin.full_name}</p>
                  <p className="text-xs text-muted-foreground">{admin.username} • {admin.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/super-admin/security')}>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Security Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Layout */}
        <div className="flex pt-12 min-h-screen">
          {/* Sidebar */}
          <aside
            className={`fixed top-12 left-0 h-[calc(100vh-3rem)] ${themeClasses.sidebarBg} transition-all duration-200 z-40 flex flex-col ${
              sidebarCollapsed ? 'w-14' : 'w-56'
            } ${
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            } md:translate-x-0`}
          >
            {/* Navigation */}
            <ScrollArea className="flex-1 py-3">
              <nav className={`space-y-1 ${sidebarCollapsed ? 'px-2' : 'px-2'}`}>
                {filteredNavItems.map((item) => (
                  <NavItem key={item.path} {...item} />
                ))}
              </nav>
            </ScrollArea>

            {/* Collapse Toggle */}
            <div className={`border-t ${themeClasses.borderColor} p-2 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    data-testid="sidebar-collapse-btn"
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${themeClasses.navItem} transition-colors ${
                      sidebarCollapsed ? 'justify-center w-10 h-10' : 'w-full'
                    }`}
                  >
                    {sidebarCollapsed ? (
                      <PanelLeftOpen className="h-4 w-4" />
                    ) : (
                      <>
                        <PanelLeftClose className="h-4 w-4" />
                        <span className="hidden md:inline">Collapse</span>
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right">Expand sidebar</TooltipContent>
                )}
              </Tooltip>
            </div>
          </aside>

          {/* Overlay for mobile */}
          {mobileMenuOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-30 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className={`flex-1 transition-all duration-200 ${
            sidebarCollapsed ? 'md:ml-14' : 'md:ml-56'
          }`}>
            <div className="p-4 md:p-6">
              {admin?.impersonation?.active && (
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-amber-500/50 bg-amber-500/15 px-4 py-3" data-testid="impersonation-banner">
                  <div className="flex items-center gap-2 text-sm">
                    <Eye className="h-4 w-4 text-amber-500 shrink-0" />
                    <span className={isDark ? 'text-amber-200' : 'text-amber-800'}>
                      Viewing as <strong>{admin.full_name}</strong> ({admin.role}) · impersonated by {admin.impersonation.impersonated_by_name || admin.impersonation.impersonated_by_email}. Session expires in 30 min.
                    </span>
                  </div>
                  <button
                    onClick={handleStopImpersonation}
                    className="shrink-0 rounded-md bg-amber-500 px-3 py-1.5 text-sm font-semibold text-black hover:bg-amber-400 transition-colors"
                    data-testid="exit-impersonation-btn"
                  >
                    Exit impersonation
                  </button>
                </div>
              )}
              <Outlet context={{ theme, isDark }} />
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default SuperAdminLayout;
